import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, Download, Edit, PackagePlus, Trash2, Upload, ArrowUpCircle, ArrowDownCircle } from 'lucide-react'
import { toast } from 'sonner'
import { giftProductsApi, type GiftProduct, type GiftProductSavePayload, type GiftProductStockAdjustPayload } from '@/api/gift-products'
import { PermissionGuard } from '@/components/auth/PermissionGuard'
import { DEFAULT_PAGE_SIZE, ListPagination } from '@/components/common/ListPagination'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent } from '@/components/ui/card'
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { showError } from '@/lib/error'
import { GiftProductDialog } from './components/GiftProductDialog'
import { GiftProductFilterBar, type GiftProductFilterValues } from './components/GiftProductFilterBar'
import { GiftProductStockAdjustDialog } from './components/GiftProductStockAdjustDialog'

const stateConfig = {
	0: { label: '上架', variant: 'default' as const },
	1: { label: '下架', variant: 'secondary' as const },
}

export function GiftProductsPage() {
	const queryClient = useQueryClient()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [filters, setFilters] = useState<GiftProductFilterValues>({})
	const [searchFilters, setSearchFilters] = useState<GiftProductFilterValues>({})
	const [page, setPage] = useState(1)
	const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
	const [selected, setSelected] = useState<number[]>([])
	const [editing, setEditing] = useState<GiftProduct | null>(null)
	const [stockAdjusting, setStockAdjusting] = useState<GiftProduct | null>(null)
	const [dialogOpen, setDialogOpen] = useState(false)
	const [stockDialogOpen, setStockDialogOpen] = useState(false)

	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['gift-products', searchFilters, page, pageSize],
		queryFn: () => giftProductsApi.list({
			...searchFilters,
			sort: 'id',
			order: 'descending',
			page,
			page_size: pageSize,
		}),
	})

	const invalidateList = () => {
		queryClient.invalidateQueries({ queryKey: ['gift-products'] })
		setSelected([])
	}

	const saveMutation = useMutation({
		mutationFn: (payload: GiftProductSavePayload) => (
			editing ? giftProductsApi.update(editing.id, payload) : giftProductsApi.create(payload)
		),
		onSuccess: () => {
			invalidateList()
			setDialogOpen(false)
			setEditing(null)
			toast.success('礼物已保存')
		},
		onError: (error) => showError(error, '保存失败'),
	})

	const deleteMutation = useMutation({
		mutationFn: (product: GiftProduct) => giftProductsApi.delete(product.id),
		onSuccess: () => {
			invalidateList()
			toast.success('礼物已删除')
		},
		onError: (error) => showError(error, '删除失败'),
	})

	const adjustStockMutation = useMutation({
		mutationFn: (payload: GiftProductStockAdjustPayload) => {
			if (!stockAdjusting) throw new Error('请选择礼物')
			return giftProductsApi.adjustStock(stockAdjusting.id, payload)
		},
		onSuccess: () => {
			invalidateList()
			setStockDialogOpen(false)
			setStockAdjusting(null)
			toast.success('库存已调整')
		},
		onError: (error) => showError(error, '调整库存失败'),
	})

	const batchDeleteMutation = useMutation({
		mutationFn: () => giftProductsApi.batchDelete(selected),
		onSuccess: () => {
			invalidateList()
			toast.success('批量删除成功')
		},
		onError: (error) => showError(error, '批量删除失败'),
	})

	const batchUpMutation = useMutation({
		mutationFn: () => giftProductsApi.batchUp(selected),
		onSuccess: () => {
			invalidateList()
			toast.success('批量上架成功')
		},
		onError: (error) => showError(error, '批量上架失败'),
	})

	const batchDownMutation = useMutation({
		mutationFn: () => giftProductsApi.batchDown(selected),
		onSuccess: () => {
			invalidateList()
			toast.success('批量下架成功')
		},
		onError: (error) => showError(error, '批量下架失败'),
	})

	const importMutation = useMutation({
		mutationFn: (file: File) => giftProductsApi.import(file),
		onSuccess: (result) => {
			invalidateList()
			toast.success(`导入完成：成功 ${result.success} 条，失败 ${result.failed} 条`)
		},
		onError: (error) => showError(error, '导入失败'),
	})

	const products = data?.list ?? []
	const hasSelection = selected.length > 0
	const allSelected = products.length > 0 && products.every((product) => selected.includes(product.id))

	const handleSearch = () => {
		setSearchFilters({ name: filters.name?.trim() ?? '', state: filters.state ?? '' })
		setPage(1)
	}

	const handleReset = () => {
		setFilters({})
		setSearchFilters({})
		setPage(1)
	}

	const handleSelectAll = (checked: boolean) => {
		setSelected(checked ? products.map((product) => product.id) : [])
	}

	const handleSelectOne = (id: number, checked: boolean) => {
		setSelected((current) => checked ? [...current, id] : current.filter((item) => item !== id))
	}

	const handleDelete = (product: GiftProduct) => {
		if (!window.confirm(`确定删除礼物「${product.name}」吗？`)) return
		deleteMutation.mutate(product)
	}

	const openStockAdjustDialog = (product: GiftProduct) => {
		setStockAdjusting(product)
		setStockDialogOpen(true)
	}

	const handleImportChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		event.target.value = ''
		if (!file) return
		importMutation.mutate(file)
	}

	return (
		<div className="space-y-5">
			<div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<h1 className="text-2xl font-bold tracking-tight">礼物管理</h1>
					<p className="mt-1 text-sm text-muted-foreground">维护礼物类型、库存数量和上下架状态</p>
				</div>
				<PermissionGuard permission="gift_product:create">
					<Button type="button" onClick={() => {
						setEditing(null)
						setDialogOpen(true)
					}}>
						<PackagePlus className="mr-2 h-4 w-4" />
						新增礼物
					</Button>
				</PermissionGuard>
			</div>

			<GiftProductFilterBar
				value={filters}
				onChange={setFilters}
				onSearch={handleSearch}
				onReset={handleReset}
				loading={isFetching}
				actions={(
					<PermissionGuard permission="gift_product:import">
						<Button type="button" size="sm" variant="outline" onClick={() => giftProductsApi.template()}>
							<Download className="mr-1.5 h-4 w-4" />
							下载模板
						</Button>
						<Button type="button" size="sm" variant="outline" disabled={importMutation.isPending} onClick={() => fileInputRef.current?.click()}>
							<Upload className="mr-1.5 h-4 w-4" />
							导入 Excel
						</Button>
						<input ref={fileInputRef} type="file" accept=".xls,.xlsx" className="hidden" onChange={handleImportChange} />
					</PermissionGuard>
				)}
			/>

			<Card>
				<CardContent className="p-0">
					{hasSelection && (
						<div className="flex items-center justify-between gap-3 border-b px-4 py-3">
							<span className="text-sm text-muted-foreground">已选择 {selected.length} 项</span>
							<div className="flex flex-wrap items-center gap-2">
								<PermissionGuard permission="gift_product:delete">
									<Button type="button" size="sm" variant="outline" disabled={batchDeleteMutation.isPending} onClick={() => batchDeleteMutation.mutate()}>
										<Trash2 className="mr-1.5 h-3.5 w-3.5" />
										批量删除
									</Button>
								</PermissionGuard>
								<PermissionGuard permission="gift_product:status">
									<Button type="button" size="sm" variant="outline" disabled={batchUpMutation.isPending} onClick={() => batchUpMutation.mutate()}>
										<ArrowUpCircle className="mr-1.5 h-3.5 w-3.5" />
										批量上架
									</Button>
									<Button type="button" size="sm" variant="outline" disabled={batchDownMutation.isPending} onClick={() => batchDownMutation.mutate()}>
										<ArrowDownCircle className="mr-1.5 h-3.5 w-3.5" />
										批量下架
									</Button>
								</PermissionGuard>
							</div>
						</div>
					)}
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="w-12">
									<Checkbox
										aria-label="选择全部礼物"
										checked={allSelected}
										onCheckedChange={(checked) => handleSelectAll(checked === true)}
									/>
								</TableHead>
								<TableHead className="w-20">ID</TableHead>
								<TableHead>名称</TableHead>
								<TableHead className="w-32">库存数量</TableHead>
								<TableHead className="w-28">状态</TableHead>
								<TableHead className="w-48">创建时间</TableHead>
								<TableHead className="w-40 text-right">操作</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={7} className="h-28 text-center text-muted-foreground">加载中...</TableCell>
								</TableRow>
							) : products.length === 0 ? (
								<TableRow>
									<TableCell colSpan={7} className="h-28 text-center text-muted-foreground">暂无礼物</TableCell>
								</TableRow>
							) : (
								products.map((product) => {
									const config = stateConfig[product.state] ?? stateConfig[1]
									return (
										<TableRow key={product.id}>
											<TableCell>
												<Checkbox
													aria-label={`选择${product.name}`}
													checked={selected.includes(product.id)}
													onCheckedChange={(checked) => handleSelectOne(product.id, checked === true)}
												/>
											</TableCell>
											<TableCell>{product.id}</TableCell>
											<TableCell className="font-medium">{product.name}</TableCell>
											<TableCell>{product.stock}</TableCell>
											<TableCell>
												<Badge variant={config.variant}>{config.label}</Badge>
											</TableCell>
											<TableCell>{formatDateTime(product.created_at)}</TableCell>
											<TableCell className="text-right">
												<div className="flex justify-end gap-2">
													<PermissionGuard permission="gift_product:update">
														<Button type="button" size="sm" variant="outline" onClick={() => {
															setEditing(product)
															setDialogOpen(true)
														}}>
															<Edit className="mr-1.5 h-4 w-4" />
															编辑
														</Button>
													</PermissionGuard>
													<PermissionGuard permission="gift_product:stock_adjust">
														<Button type="button" size="sm" variant="outline" onClick={() => openStockAdjustDialog(product)}>
															<Archive className="mr-1.5 h-4 w-4" />
															调整库存
														</Button>
													</PermissionGuard>
													<PermissionGuard permission="gift_product:delete">
														<Button type="button" size="sm" variant="destructive" onClick={() => handleDelete(product)}>
															<Trash2 className="mr-1.5 h-4 w-4" />
															删除
														</Button>
													</PermissionGuard>
												</div>
											</TableCell>
										</TableRow>
									)
								})
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<ListPagination
				page={page}
				pageSize={pageSize}
				total={data?.total ?? 0}
				loading={isFetching}
				onPageChange={setPage}
				onPageSizeChange={(nextPageSize) => {
					setPageSize(nextPageSize)
					setPage(1)
				}}
			/>

			<GiftProductDialog
				open={dialogOpen}
				product={editing}
				isSaving={saveMutation.isPending}
				onOpenChange={(open) => {
					setDialogOpen(open)
					if (!open) setEditing(null)
				}}
				onSubmit={(payload) => saveMutation.mutate(payload)}
			/>
			<GiftProductStockAdjustDialog
				open={stockDialogOpen}
				product={stockAdjusting}
				isSaving={adjustStockMutation.isPending}
				onOpenChange={(open) => {
					setStockDialogOpen(open)
					if (!open) setStockAdjusting(null)
				}}
				onSubmit={(payload) => adjustStockMutation.mutate(payload)}
			/>
		</div>
	)
}

function formatDateTime(value: string) {
	if (!value) return '-'
	const date = new Date(value)
	if (Number.isNaN(date.getTime())) return value
	return date.toLocaleString('zh-CN', { hour12: false })
}
